import { useState } from "react";
import { toast } from "sonner";
import { Phone, Clock, Loader2, MailCheck } from "lucide-react";
import { Reveal, Eyebrow } from "@/components/Reveal";
import { api } from "@/lib/api";
import { StayConnected } from "@/components/StayConnected";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/contact", form);
      setDone(true);
      toast.success("Message received — we'll write back soon.");
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full rounded-xl border border-line bg-white px-4 py-3 text-sm text-ink placeholder:text-taupe outline-none transition-colors focus:border-gold";

  return (
    <div className="mx-auto max-w-7xl px-4 pb-24 pt-14 sm:px-6 lg:px-8 lg:pt-20" data-testid="contact-page">
      <Reveal>
        <Eyebrow>Say hello</Eyebrow>
        <h1
          className="mt-4 font-serif text-4xl font-bold leading-[1.15] tracking-tight text-ink sm:text-5xl lg:text-6xl"
          data-testid="contact-headline"
        >
          We'd love to <span className="italic text-crimson">hear from you.</span>
        </h1>
      </Reveal>

      <div className="mt-14 grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-14">
        <Reveal className="lg:col-span-7">
          <div className="rounded-3xl border border-line bg-white p-8 shadow-sm sm:p-10" data-testid="contact-form-card">
            {done ? (
              <div className="flex flex-col items-start gap-4 py-8" data-testid="contact-form-success">
                <MailCheck className="h-10 w-10 text-gold" />
                <h2 className="font-serif text-2xl font-bold text-ink sm:text-3xl">
                  Your note is on its way.
                </h2>
                <p className="max-w-md text-base leading-relaxed text-cocoa">
                  Thank you for reaching out. A real human — probably Amanda —
                  will get back to you soon.
                </p>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-5" data-testid="contact-form">
                <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                  <div>
                    <label htmlFor="contact-name" className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.12em] text-cocoa">Name</label>
                    <input id="contact-name" type="text" required value={form.name} onChange={set("name")} placeholder="Your name" className={inputCls} data-testid="contact-name-input" />
                  </div>
                  <div>
                    <label htmlFor="contact-email" className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.12em] text-cocoa">Email</label>
                    <input id="contact-email" type="email" required value={form.email} onChange={set("email")} placeholder="you@example.com" className={inputCls} data-testid="contact-email-input" />
                  </div>
                </div>
                <div>
                  <label htmlFor="contact-subject" className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.12em] text-cocoa">Subject</label>
                  <input id="contact-subject" type="text" required value={form.subject} onChange={set("subject")} placeholder="What's on your heart?" className={inputCls} data-testid="contact-subject-input" />
                </div>
                <div>
                  <label htmlFor="contact-message" className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.12em] text-cocoa">Message</label>
                  <textarea id="contact-message" required rows={5} value={form.message} onChange={set("message")} placeholder="Tell us everything." className={inputCls} data-testid="contact-message-input" />
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  className="inline-flex items-center gap-2 rounded-full bg-crimson px-8 py-3.5 text-sm font-semibold text-ivory transition-all duration-200 hover:-translate-y-0.5 hover:bg-crimson-deep disabled:opacity-60"
                  data-testid="contact-form-submit-button"
                >
                  {loading && <Loader2 className="h-4 w-4 animate-spin" />}
                  Send Message
                </button>
              </form>
            )}
          </div>
        </Reveal>

        <div className="lg:col-span-5">
          <Reveal delay={0.1}>
            <div className="rounded-3xl border border-gold/50 bg-gold-soft p-8" data-testid="contact-info-card">
              <div className="flex items-start gap-4">
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-crimson text-ivory">
                  <Phone className="h-5 w-5" />
                </div>
                <div>
                  <p
                    className="font-serif text-2xl font-bold text-ink"
                    data-testid="contact-phone-label"
                  >
                    Phone
                  </p>
                  <p className="mt-1 text-sm text-cocoa">
                    Give us a call — Amanda answers, and she's lovely.
                  </p>
                </div>
              </div>
              <div className="mt-8 flex items-start gap-4 border-t border-gold/40 pt-8">
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-white text-crimson">
                  <MailCheck className="h-5 w-5" />
                </div>
                <div>
                  <a
                    href="mailto:info@myheartgives.com"
                    className="font-serif text-lg font-bold text-ink transition-colors hover:text-crimson"
                    data-testid="contact-email-link"
                  >
                    info@myheartgives.com
                  </a>
                  <p className="mt-1 text-sm text-cocoa">
                    We read every note ourselves.
                  </p>
                </div>
              </div>
              <div className="mt-8 flex items-start gap-4 border-t border-gold/40 pt-8">
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-white text-crimson">
                  <Clock className="h-5 w-5" />
                </div>
                <div>
                  <p className="font-serif text-lg font-semibold text-ink">A note about John</p>
                  <p className="mt-1 text-sm leading-relaxed text-cocoa" data-testid="john-availability-note">
                    John is available after 6PM Eastern for cause partnerships
                    and media inquiries.
                  </p>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
      <StayConnected testidPrefix="contact-stay-connected" />
    </div>
  );
}
